import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from '../../utils/toast';
import PullToRefresh from '../UI/PullToRefresh';
import PasswordChangeModal from '../UI/PasswordChangeModal';
import { CountBadge } from '../UI/CountBadge';
import { 
  Menu, 
  X, 
  LayoutDashboard, 
  User, 
  Calendar, 
  CalendarDays,
  Clock, 
  FileText, 
  Bell,
  ChevronDown,
  LogOut,
  Settings,
  Shield,
  ArrowRight,
  DollarSign,
  Laptop,
  BookOpen,
  Users,
  Package,
  Key,
  UsersRound,
  Truck,
  HelpCircle,
  ClipboardList,
  FileSpreadsheet
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useScrollMainToTop } from '../../hooks/useScrollMainToTop';
import { useMobileSidebarOpen } from '../../hooks/useMobileSidebarOpen';
import { useSidebarCollapsed } from '../../hooks/useSidebarCollapsed';
import { useViewportAnchoredPanel, USER_MENU_PANEL_OPTIONS, getViewportPanelStyle } from '../../hooks/useViewportAnchoredPanel';
import { useMyAssetsSummary } from '../../hooks/useAssetInventoryData';
import { usePassportReturnSummary } from '../../hooks/usePassportReturns';
import { useTransportPortalAccess } from '../../hooks/useTransportPortalAccess';
import {
  countOpenEmployeeOffboardingTasks,
  useAssignedOffboardingTasks,
  useMyOffboardingCase,
} from '../../hooks/useOffboardingCases';
import { authAPI, staffAPI } from '../../services/api';
import { fetchRoPendingApprovals } from '../../utils/pendingLeaveInbox';
import NotificationDropdown from '../UI/NotificationDropdown';
import QuickActionsFAB from '../UI/QuickActionsFAB';
import { useQuickActions } from '../../hooks/useQuickActions';
import NameCardQuickShare from '../namecard/NameCardQuickShare';
import { getClassificationAvatarHex } from '../../utils/classificationConfig';
import weplLogo from '../../assets/images/wepl_logo.png';

const DashboardLayout = ({ children }) => {
  const { user, logout, canAccessAdmin } = useAuth();
  const { sidebarOpen, setSidebarOpen, onOverlayClick, menuButtonProps } = useMobileSidebarOpen();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed(user?.staff_id || user?.id);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState(null);
  const mainScrollRef = useRef(null);
  const scrollMainToTop = useScrollMainToTop(mainScrollRef);
  const userMenuRef = useClickOutside(() => setUserMenuOpen(false), userMenuOpen);
  const notificationRef = useClickOutside(() => setNotificationOpen(false), notificationOpen);
  const { panelRect: userMenuPanelRect } = useViewportAnchoredPanel({
    open: userMenuOpen,
    anchorRef: userMenuRef,
    ...USER_MENU_PANEL_OPTIONS,
  });
  const { notifications, unreadCount, loading, error, markAsRead, markAllAsRead } = useNotifications();
  const { data: assetsSummary } = useMyAssetsSummary();
  const { data: transportPortalAccess } = useTransportPortalAccess({
    enabled: !!user,
  });
  const location = useLocation();
  
  // Calculate pending transactions count
  const pendingTransactionsCount = useMemo(() => {
    if (!assetsSummary) return 0;
    return (assetsSummary.pending_incoming?.length || 0) +
           (assetsSummary.pending_outgoing?.length || 0) +
           (assetsSummary.pending_receipt?.length || 0) +
           (assetsSummary.pending_returns?.length || 0);
  }, [assetsSummary]);

  const { data: passportSummary } = usePassportReturnSummary({
    enabled: !!user,
  });
  const pendingPassportActionCount = passportSummary?.pending_my_action || 0;

  const { data: assignedOffboardingTasks = [] } = useAssignedOffboardingTasks({
    enabled: !!user,
  });
  const assignedOffboardingCount = assignedOffboardingTasks.length;
  const onOffboardingTasksPage = location.pathname === '/offboarding-tasks';
  // Inbox, not a standing role tool: only surface when this person has
  // clearance to sign, or they are already on the page (so the item does
  // not vanish under them after the last sign-off).
  const showOffboardingTasksNav = onOffboardingTasksPage || assignedOffboardingCount > 0;

  const { data: myOffboardingCase } = useMyOffboardingCase({
    enabled: !!user,
  });
  const pendingOffboardingSelfCount = countOpenEmployeeOffboardingTasks(myOffboardingCase);

  // Role flags for dashboard badge queries
  const isAdminAccess = useMemo(() => !!(canAccessAdmin && canAccessAdmin()), [canAccessAdmin, user]);
  // Any leave-approval responsibility, incl. Trade Leader (L2) and HOD/Deputy
  // HOD (L3) who are not necessarily reporting officers. Mirrors Dashboard.js.
  const isApprover = !!(
    user?.is_reporting_officer ||
    user?.is_manager ||
    user?.is_trade_leader ||
    user?.is_hod ||
    (user?.team_members?.length > 0)
  );

  // Dashboard badge: pending leave approvals (RO/managers only)
  // Uses same queryKey as Dashboard.js so React Query shares the cache
  const { data: pendingApprovalsCount = 0 } = useQuery({
    queryKey: ['pendingApprovals', user?.staff_id],
    queryFn: async () => {
      try {
        return await fetchRoPendingApprovals();
      } catch {
        return [];
      }
    },
    enabled: !!user && isApprover,
    staleTime: 5 * 60 * 1000,
    select: (items) => (Array.isArray(items) ? items.length : 0),
  });

  // Dashboard badge: pending doc verifications (admin only)
  const { data: pendingVerificationCount = 0 } = useQuery({
    queryKey: ['pendingDocApprovalsSummary'],
    queryFn: () => staffAPI.getPendingDocumentApprovalsSummary(),
    enabled: !!user && isAdminAccess,
    staleTime: 5 * 60 * 1000,
    select: (response) => response?.data?.total_pending_documents || 0,
  });

  // Mirror Dashboard.js visibility guard: admin-only users (not approvers) don't see My Approvals
  const countableApprovals = (isAdminAccess && !isApprover) ? 0 : pendingApprovalsCount;
  const dashboardBadgeCount = countableApprovals + pendingVerificationCount;

  const isExecutiveDirector = user?.employment_classification === 'executive_director';

  const navigation = [
    // === DAILY USE ===
    {
      name: 'Dashboard',
      href: '/',
      icon: LayoutDashboard,
      current: location.pathname === '/' || location.pathname === '/dashboard',
      description: 'Overview & Quick Actions',
      badge: dashboardBadgeCount
    },
    // Executive Directors are exempt from attendance tracking
    ...(!isExecutiveDirector ? [{
      name: 'Attendance',
      href: '/attendance',
      icon: Clock,
      current: location.pathname === '/attendance',
      description: 'Clock In/Out & Time Tracking'
    }] : []),
    // ROs, HODs & Lead Trade Leaders check their team daily — kept near the top
    ...((user?.is_reporting_officer || user?.is_hod || user?.is_lead_trade_leader) ? [{
      name: 'My Team',
      href: '/team-dashboard',
      icon: UsersRound,
      current: location.pathname.startsWith('/team-dashboard'),
      description: (user?.is_hod || user?.is_lead_trade_leader)
        ? 'Team & Department Attendance & Wellbeing'
        : "Your Direct Reports' Attendance & Wellbeing"
    }] : []),
    // === WEEKLY USE ===
    {
      name: 'Leave Management',
      href: '/leave',
      icon: Calendar,
      current: location.pathname.startsWith('/leave') && location.pathname !== '/leave-calendar',
      description: 'Apply & Track Leave Requests'
    },
    {
      name: 'Leave Calendar',
      href: '/leave-calendar',
      icon: CalendarDays,
      current: location.pathname === '/leave-calendar',
      description: 'View Department & Company Leave Schedules'
    },
    ...(showOffboardingTasksNav ? [{
      name: 'Offboarding Tasks',
      href: '/offboarding-tasks',
      icon: ClipboardList,
      current: onOffboardingTasksPage,
      description: 'Sign off clearance for a departing colleague',
      badge: assignedOffboardingCount,
    }] : []),
    // === MONTHLY USE ===
    {
      name: 'Payslips',
      href: '/payslips',
      icon: FileText,
      current: location.pathname === '/payslips',
      description: 'Salary Statements & History'
    },
    ...(user?.receives_worker_cost_digest ? [{
      name: 'Worker Cost',
      href: '/worker-cost',
      icon: FileSpreadsheet,
      current: location.pathname === '/worker-cost',
      description: 'Monthly worker salary and levy file'
    }] : []),
    {
      name: 'Petty Cash Claims',
      href: '/petty-cash',
      icon: DollarSign,
      current: location.pathname === '/petty-cash',
      description: 'Submit & Track Expense Claims',
    },
    ...(transportPortalAccess?.has_transport_access === true ? [{
      name: 'Transport',
      href: '/transport',
      icon: Truck,
      current: location.pathname === '/transport',
      description: 'Book & Manage Transport'
    }] : []),
    // === OCCASIONAL USE ===
    {
      name: 'My Profile',
      href: '/profile',
      icon: User,
      current: location.pathname === '/profile',
      description: 'Personal Information & Documents'
    },
    {
      name: 'Staff Directory',
      href: '/staff-directory',
      icon: Users,
      current: location.pathname === '/staff-directory',
      description: 'Find Colleagues & Organizational Structure'
    },
    {
      name: 'My Assets',
      href: '/my-assets',
      icon: Laptop,
      current: location.pathname.startsWith('/my-assets'),
      description: 'Company Equipment & Resources',
      badge: pendingTransactionsCount
    },
    {
      name: 'Learning & Development',
      href: '/learning-development',
      icon: BookOpen,
      current: location.pathname === '/learning-development',
      description: 'Training & Professional Development'
    },
    {
      name: 'Self-Service',
      href: '/self-service',
      icon: Settings,
      current: location.pathname.startsWith('/self-service'),
      description: 'Requests, Handbook & Policies',
      badge: pendingPassportActionCount + pendingOffboardingSelfCount,
    },
    // === HELP === always-available reference, kept last
    {
      name: 'User Guide',
      href: '/user-guide',
      icon: HelpCircle,
      current: location.pathname === '/user-guide',
      description: 'Step-by-Step Help & How-Tos'
    },
  ];

  const handleLogout = () => {
    logout();
  };

  // Handle voluntary password change
  const handlePasswordChange = async (currentPassword, newPassword) => {
    setPasswordChangeLoading(true);
    setPasswordChangeError(null);
    try {
      const response = await authAPI.changePassword(currentPassword, newPassword);
      if (response?.data?.success || response?.status === 200) {
        setPasswordModalOpen(false);
        toast.success('Password changed successfully! You have been logged out of other devices.');
      } else {
        setPasswordChangeError('Failed to change password. Please try again.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to change password. Please try again.';
      setPasswordChangeError(errorMessage);
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Route exclusion logic - hide FAB on auth/error pages
  const shouldHideFAB = useMemo(() => {
    const excludedRoutes = [
      '/login', '/forgot-password', '/reset-password',
      '/404', '/500', '/unauthorized'
    ];
    return excludedRoutes.some(route => location.pathname.startsWith(route));
  }, [location.pathname]);

  // Get employee quick actions
  const [showNameCardOverlay, setShowNameCardOverlay] = useState(false);
  const handleOpenNameCard = useCallback(() => setShowNameCardOverlay(true), []);
  const quickActions = useQuickActions(false, 0, {
    onOpenNameCard: handleOpenNameCard,
    classification: user?.employment_classification,
  });

  return (
    <div className="flex h-dvh bg-gray-50 overflow-hidden">
      {/* Mobile sidebar overlay - tap anywhere outside to close */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onOverlayClick} />
          <div className="fixed top-0 left-0 w-80 h-full bg-white shadow-xl flex flex-col">
            <SidebarContent
              navigation={navigation}
              sidebarCollapsed={false}
              setSidebarCollapsed={setSidebarCollapsed}
              setSidebarOpen={setSidebarOpen}
              user={user}
              canAccessAdmin={canAccessAdmin}
              weplLogo={weplLogo}
              isMobile={true}
            />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={`hidden lg:flex ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-80'} lg:flex-col lg:flex-shrink-0 transition-all duration-300`}>
        <SidebarContent
          navigation={navigation}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          setSidebarOpen={setSidebarOpen}
          user={user}
          canAccessAdmin={canAccessAdmin}
          weplLogo={weplLogo}
          isMobile={false}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col lg:ml-0 min-w-0 overflow-hidden">
        {/* Header — fixed inside the locked shell. paddingTop respects the iOS
            notch / Android cutout in PWA standalone mode. touchAction:'manipulation'
            blocks pan gestures so the page can never be dragged from the top bar.
            Empty-area tap scrolls <main> to top on mobile only (iOS/Android
            polyfill — see useScrollMainToTop / App Shell DS; no-op at lg+). */}
        <header
          onClick={scrollMainToTop}
          className="bg-white shadow-sm border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            height: 'calc(4rem + env(safe-area-inset-top))',
            touchAction: 'manipulation',
          }}
        >
          <div className="flex items-center space-x-6">
            <button
              {...menuButtonProps}
              className="icon-button lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </button>
          <Link to="/" className="flex items-center shrink-0" aria-label="Go to dashboard">
            <img src={weplLogo} alt="Company logo" className="h-7 w-auto" />
          </Link>
          </div>

          <div className="flex items-center space-x-4">

            {/* Notifications — click-outside ref wraps bell + panel (DS dismissal pattern) */}
            <div className="relative" ref={notificationRef}>
              <button 
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  setNotificationOpen((open) => !open);
                }}
                className="icon-button relative"
                aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
                aria-expanded={notificationOpen}
                aria-haspopup="dialog"
              >
                <Bell 
                  className={`w-6 h-6 ${unreadCount > 0 ? 'text-red-500 fill-current animate-pulse' : ''}`} 
                />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                )}
              </button>

              {notificationOpen && (
                <NotificationDropdown
                  notifications={notifications}
                  unreadCount={unreadCount}
                  loading={loading}
                  error={error}
                  onClose={() => setNotificationOpen(false)}
                  onMarkAsRead={markAsRead}
                  onMarkAllAsRead={markAllAsRead}
                />
              )}
            </div>

            {/* User Menu */}
            <div className="relative shrink-0" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setNotificationOpen(false);
                  setUserMenuOpen((open) => !open);
                }}
                className="flex items-center space-x-3 p-2 rounded-full hover:bg-gray-100 transition-all duration-300"
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center shadow-sm"
                  style={{ 
                    backgroundColor: getClassificationAvatarHex(user?.employment_classification)
                  }}
                  title={`Classification: ${user?.employment_classification || 'Unknown'}`}
                >
                  <span className="text-sm font-semibold text-white">
                    {user?.full_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-semibold text-gray-700">
                    {user?.full_name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 font-mono">
                    {user?.staff_id || '--'}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {/* User Dropdown */}
              {userMenuOpen && (
                <div
                  className="fixed z-[70] rounded-2xl border border-gray-200 bg-white py-2 shadow-lg"
                  style={getViewportPanelStyle(userMenuPanelRect)}
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-700">
                      {user?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 font-mono">
                      {user?.staff_id || '--'}
                    </p>
                    {user?.work_email && (
                      <p className="text-xs text-gray-400 mt-1 break-all">
                        {user.work_email}
                      </p>
                    )}
                  </div>
                  
                  {/* Role Switching */}
                  {(canAccessAdmin() || user?.is_asset_custodian) && (
                    <div className="px-3 py-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-1">Switch Role</p>
                      {canAccessAdmin() && (
                      <Link
                        to="/admin"
                        className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-lg transition-all duration-300 group"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Shield className="w-4 h-4 mr-3 shrink-0 text-gray-400 group-hover:text-red-600" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">Admin View</p>
                          <p className="text-xs text-gray-500">Access administrative features</p>
                        </div>
                        <ArrowRight className="w-4 h-4 shrink-0 text-gray-400" />
                      </Link>
                      )}
                      {user?.is_asset_custodian && (
                        <Link
                          to="/asset-admin"
                          className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 rounded-lg transition-all duration-300 group mt-1"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <Package className="w-4 h-4 mr-3 shrink-0 text-gray-400 group-hover:text-amber-600" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">Asset Admin</p>
                            <p className="text-xs text-gray-500">Manage company assets</p>
                          </div>
                          <ArrowRight className="w-4 h-4 shrink-0 text-gray-400" />
                        </Link>
                      )}
                    </div>
                  )}
                  
                  <div className="border-t border-gray-100 my-2 mx-3" />
                  
                  <div className="px-2 space-y-0.5">
                    <Link
                      to="/profile"
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="w-4 h-4 mr-3 shrink-0" />
                      My Profile
                    </Link>

                    <Link
                      to="/user-guide"
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <HelpCircle className="w-4 h-4 mr-3 shrink-0" />
                      User Guide
                    </Link>
                    
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        setPasswordModalOpen(true);
                      }}
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                    >
                      <Key className="w-4 h-4 mr-3 shrink-0" />
                      Change Password
                    </button>
                  </div>
                  
                  <div className="border-t border-gray-100 my-2 mx-3" />
                  <div className="px-2 pb-1">
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                    >
                      <LogOut className="w-4 h-4 mr-3 shrink-0" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content — the only document-scrollable surface in the shell.
            pb-safe reserves room under the home-indicator (PWA standalone). */}
        <main ref={mainScrollRef} className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 p-4 md:p-6 pb-safe" style={{ touchAction: 'pan-y' }}>
          <PullToRefresh scrollContainerRef={mainScrollRef}>
            {children}
          </PullToRefresh>
        </main>
      </div>

      {/* Employee Quick Actions FAB - Only show when user is loaded and route is not excluded */}
      {user && !shouldHideFAB && (
        <QuickActionsFAB actions={quickActions} theme="employee" />
      )}

      {/* Digital Name Card Quick Share Overlay */}
      <NameCardQuickShare
        isOpen={showNameCardOverlay}
        onClose={() => setShowNameCardOverlay(false)}
      />

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={passwordModalOpen}
        onChangePassword={handlePasswordChange}
        onClose={() => {
          setPasswordModalOpen(false);
          setPasswordChangeError(null);
        }}
        loading={passwordChangeLoading}
        error={passwordChangeError}
        mode="voluntary"
      />
    </div>
  );
};

const SidebarContent = ({ navigation, sidebarCollapsed, setSidebarCollapsed, setSidebarOpen, user, canAccessAdmin, weplLogo, isMobile }) => {
  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Sidebar Header - Fixed */}
      <div className={`flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-6'} h-16 border-b border-gray-200 flex-shrink-0`}>
        {sidebarCollapsed ? (
          /* Collapsed state - Only show hamburger menu */
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="icon-button"
            title="Expand sidebar"
          >
            <Menu className="w-6 h-6" />
          </button>
        ) : (
          /* Expanded state - Show logo, text, and controls */
          <>
            <div className="flex items-center space-x-3">
              <img 
                src={weplLogo} 
                alt="Company Logo" 
                className="h-7 w-auto object-contain"
              />
              <div>
                <span className="text-xl font-bold text-gray-900">HR Portal</span>
                <p className="text-xs text-gray-500 font-medium">HR Management System</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isMobile && (
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="icon-button hidden lg:inline-flex"
                  title="Collapse sidebar"
                >
                  <Menu className="w-5 h-5" />
                </button>
              )}
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="icon-button lg:hidden"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Sidebar Navigation - Scrollable */}
      <nav className={`flex-1 mt-2 ${sidebarCollapsed ? 'px-2' : 'px-4'} overflow-y-auto`}>
        {/* Employee Menu */}
        <div className="space-y-2">
          {navigation.map((item) => {
            const isExternalAction = !!item.onClick;
            const Tag = isExternalAction ? 'button' : Link;
            const tagProps = isExternalAction
              ? {
                  type: 'button',
                  onClick: (e) => {
                    e.preventDefault();
                    setSidebarOpen(false);
                    item.onClick();
                  },
                }
              : {
                  to: item.href,
                  onClick: () => setSidebarOpen(false),
                };

            return (
              <Tag
                key={item.name}
                {...tagProps}
                className={`flex items-center rounded-full transition-all duration-300 group relative text-left ${
                  item.current
                    ? 'bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg hover:shadow-xl'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } ${sidebarCollapsed ? 'justify-center w-11 h-11 mx-auto' : 'w-full px-4 py-3'}`}
                title={sidebarCollapsed ? item.name : undefined}
              >
                <item.icon className={`w-6 h-6 ${sidebarCollapsed ? '' : 'mr-4'} flex-shrink-0 ${
                  item.current ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'
                } transition-colors duration-300`} />
                {!sidebarCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${
                      item.current ? 'text-white' : 'text-gray-900'
                    } transition-colors duration-300`}>
                      {item.name}
                    </p>
                    <p className={`text-xs mt-1 ${
                      item.current ? 'text-blue-100' : 'text-gray-500'
                    } transition-colors duration-300`}>
                      {item.description}
                    </p>
                  </div>
                )}
                {item.badge > 0 && (
                  <CountBadge
                    count={item.badge}
                    theme={item.current ? 'employee' : 'orange'}
                    active={item.current}
                    overlay={sidebarCollapsed}
                    overlayPosition={sidebarCollapsed ? 'inside' : 'outside'}
                    className={sidebarCollapsed ? '' : 'ml-2'}
                  />
                )}
              </Tag>
            );
          })}
        </div>
      </nav>

      {/* Admin Portals - Sticky Footer */}
      {(user?.is_asset_custodian || canAccessAdmin()) && (
        <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} py-4 border-t border-gray-200 space-y-2`}>
          {/* Asset Admin Portal - Only for Asset Custodians */}
          {user?.is_asset_custodian && (
            <Link
              to="/asset-admin"
              className={`flex items-center rounded-full transition-all duration-300 group ${
                sidebarCollapsed ? 'justify-center w-11 h-11 mx-auto' : 'w-full px-4 py-3'
              } bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg hover:shadow-xl`}
              onClick={() => setSidebarOpen(false)}
              title={sidebarCollapsed ? 'Asset Admin Portal' : undefined}
            >
              <Package
                className={`w-5 h-5 ${sidebarCollapsed ? '' : 'mr-3'} flex-shrink-0 text-white transition-colors duration-300`}
              />
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white transition-colors duration-300">
                    Asset Admin Portal
                  </p>
                  <p className="text-xs mt-0.5 text-red-100 transition-colors duration-300">
                    Manage Company Assets
                  </p>
                </div>
              )}
            </Link>
          )}

          {/* HR Admin Portal - Only for Admins */}
          {canAccessAdmin() && (
            <Link
              to="/admin"
              className={`flex items-center rounded-full transition-all duration-300 group ${
                sidebarCollapsed ? 'justify-center w-11 h-11 mx-auto' : 'w-full px-4 py-3'
              } bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg hover:shadow-xl`}
              onClick={() => setSidebarOpen(false)}
              title={sidebarCollapsed ? 'HR Admin Portal' : undefined}
            >
              <Shield
                className={`w-5 h-5 ${sidebarCollapsed ? '' : 'mr-3'} flex-shrink-0 text-white transition-colors duration-300`}
              />
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white transition-colors duration-300">
                    HR Admin Portal
                  </p>
                  <p className="text-xs mt-0.5 text-red-100 transition-colors duration-300">
                    Manage HR & Payroll
                  </p>
                </div>
              )}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;